// /apps/patient/src/pages/programs/SessionPlayer.tsx
import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Play,
  Pause,
  SkipForward,
  SkipBack,
  Volume2,
  VolumeX,
  Check,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  Clock,
  TrendingUp,
  Heart,
  AlertCircle,
  Video,
  X,
  FileText,
  Activity,
  FastForward,
  MapPin
} from 'lucide-react';
import { PageHeader } from '../../components/layout/PageHeader';
import { patientApiService } from '../../services/api';
import { format } from 'date-fns';
import {
  CardioConfig,
  CardioSegment,
  CardioSessionState,
  CardioCompletionData,
  PatientLocation,
  ExerciseSubstitution,
  ExerciseEquipmentCheck,
  SessionLocationContext
} from '@prism-ehr/shared';
import { LocationSelectionModal } from '../../components/programs/LocationSelectionModal';
import { SubstitutionBanner } from '../../components/programs/SubstitutionBanner';

// Types
interface SessionState {
  sessionId?: string;
  assignmentId: string;
  prescriptionId: string;
  sessionDate: string;
  startedAt: string;
  currentModuleIndex: number;
  currentExerciseIndex: number;
  currentSet: number;
  timerMode: 'work' | 'rest' | 'hold' | 'paused' | 'stopped';
  timerDuration: number;
  timerRemaining: number;
  audioEnabled: boolean;
  completedExercises: CompletedExercise[];
  skippedExercises: SkippedExercise[];
  totalExercisesPrescribed: number;
  // Location context
  locationContext?: SessionLocationContext;
}

interface CompletedExercise {
  exercise_id: string;
  exercise_name: string;
  module_id?: string;
  sets_completed: number;
  reps_completed: string[];
  weight_used: string[];
  difficulty_rating?: 'too_easy' | 'just_right' | 'too_hard';
  pain_level?: number;
  notes?: string;
  completed_at: string;
}

interface SkippedExercise {
  exercise_id: string;
  reason: string;
  skipped_at: string;
}

// ============= CARDIO-SPECIFIC COMPONENTS =============

interface CardioPlayerProps {
  exercise: any;
  config: CardioConfig;
  onComplete: (data: CardioCompletionData) => void;
  onSkip: () => void;
  audioEnabled: boolean;
}

const CardioPlayer: React.FC<CardioPlayerProps> = ({
  exercise,
  config,
  onComplete,
  onSkip,
  audioEnabled
}) => {
  const [sessionState, setSessionState] = useState<CardioSessionState>({
    format: config.format,
    current_segment_index: 0,
    current_repeat: 1,
    total_elapsed_seconds: 0,
    timer_mode: 'stopped',
    timer_remaining: 0,
    completed_segments: [],
    distance_covered: 0
  });

  const [timerActive, setTimerActive] = useState(false);
  const [currentDistance, setCurrentDistance] = useState<string>('');
  const [currentPace, setCurrentPace] = useState<string>('');
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const startTimeRef = useRef<number>(Date.now());

  // Initialize audio
  useEffect(() => {
    if (typeof window !== 'undefined' && window.AudioContext) {
      audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  // Play sound helper
  const playSound = (pattern: 'start' | 'end' | 'transition') => {
    if (!audioEnabled || !audioContextRef.current) return;

    const context = audioContextRef.current;
    const oscillator = context.createOscillator();
    const gainNode = context.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(context.destination);

    switch (pattern) {
      case 'start':
        oscillator.frequency.value = 523.25; // C5
        gainNode.gain.value = 0.3;
        break;
      case 'end':
        oscillator.frequency.value = 659.25; // E5
        gainNode.gain.value = 0.4;
        break;
      case 'transition':
        oscillator.frequency.value = 440; // A4
        gainNode.gain.value = 0.2;
        break;
    }

    oscillator.start();
    oscillator.stop(context.currentTime + 0.2);
  };

  // Get current segment
  const getCurrentSegment = (): CardioSegment | null => {
    if (config.format === 'simple') {
      return null;
    } else if (config.format === 'intervals' && config.intervals) {
      const segmentIndex = sessionState.current_segment_index % config.intervals.segments.length;
      return config.intervals.segments[segmentIndex];
    } else if (config.format === 'structured' && config.structured) {
      return config.structured.segments[sessionState.current_segment_index];
    }
    return null;
  };

  // Get next segment
  const getNextSegment = (): CardioSegment | null => {
    if (config.format === 'intervals' && config.intervals) {
      const nextIndex = (sessionState.current_segment_index + 1) % config.intervals.segments.length;
      return config.intervals.segments[nextIndex];
    } else if (config.format === 'structured' && config.structured) {
      const nextIndex = sessionState.current_segment_index + 1;
      if (nextIndex < config.structured.segments.length) {
        return config.structured.segments[nextIndex];
      }
    }
    return null;
  };

  // Calculate total duration
  const getTotalDuration = (): number => {
    if (config.format === 'simple' && config.simple) {
      return (config.simple.duration_minutes || 0) * 60;
    } else if (config.format === 'intervals' && config.intervals) {
      const segmentDuration = config.intervals.segments.reduce(
        (sum, seg) => sum + (seg.duration_seconds || 0), 0
      );
      return segmentDuration * (config.intervals.repeat_count || 1);
    } else if (config.format === 'structured' && config.structured) {
      return config.structured.segments.reduce(
        (sum, seg) => sum + (seg.duration_seconds || 0), 0
      );
    }
    return 0;
  };

  // Start timer
  const startTimer = () => {
    playSound('start');
    setTimerActive(true);
    startTimeRef.current = Date.now() - (sessionState.total_elapsed_seconds * 1000);

    let duration: number;
    const currentSegment = getCurrentSegment();

    if (config.format === 'simple' && config.simple) {
      duration = (config.simple.duration_minutes || 20) * 60 - sessionState.total_elapsed_seconds;
    } else if (currentSegment) {
      duration = (currentSegment.duration_seconds || 60) -
        (sessionState.timer_remaining > 0 ?
          (currentSegment.duration_seconds || 60) - sessionState.timer_remaining : 0);
    } else {
      duration = 60; // Default
    }

    setSessionState(prev => ({
      ...prev,
      timer_mode: currentSegment?.type === 'recovery' ? 'rest' : 'work',
      timer_remaining: duration
    }));

    timerRef.current = setInterval(() => {
      setSessionState(prev => {
        const newRemaining = prev.timer_remaining - 1;
        const newElapsed = prev.total_elapsed_seconds + 1;

        if (newRemaining <= 3 && newRemaining > 0) {
          // Move playSound outside setState to avoid render issues
          setTimeout(() => playSound('transition'), 0);
        }

        if (newRemaining <= 0) {
          // Move handleSegmentComplete outside setState to avoid the warning
          setTimeout(() => handleSegmentComplete(), 0);
          return prev; // Return unchanged state
        }

        return {
          ...prev,
          timer_remaining: newRemaining,
          total_elapsed_seconds: newElapsed
        };
      });
    }, 1000);
  };

  // Pause timer
  const pauseTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      setTimerActive(false);
      setSessionState(prev => ({ ...prev, timer_mode: 'paused' }));
    }
  };

  // Handle segment complete
  const handleSegmentComplete = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    setTimerActive(false);
    playSound('end');

    const currentSegment = getCurrentSegment();

    // Record completed segment
    const completedSegment = {
      segment_index: sessionState.current_segment_index,
      repeat_number: sessionState.current_repeat,
      duration: currentSegment?.duration_seconds || 0,
      distance: currentDistance ? parseFloat(currentDistance) : undefined
    };

    setSessionState(prev => ({
      ...prev,
      completed_segments: [...prev.completed_segments, completedSegment]
    }));

    // Move to next segment
    if (config.format === 'simple') {
      // Simple format - we're done
      handleWorkoutComplete();
    } else if (config.format === 'intervals' && config.intervals) {
      const segmentsPerRound = config.intervals.segments.length;
      const nextSegmentIndex = sessionState.current_segment_index + 1;

      if (nextSegmentIndex >= segmentsPerRound * sessionState.current_repeat) {
        // Completed a full round
        if (sessionState.current_repeat < (config.intervals.repeat_count || 1)) {
          // More rounds to go
          setSessionState(prev => ({
            ...prev,
            current_segment_index: nextSegmentIndex,
            current_repeat: prev.current_repeat + 1,
            timer_mode: 'stopped'
          }));
        } else {
          // All rounds complete
          handleWorkoutComplete();
        }
      } else {
        // Next segment in current round
        setSessionState(prev => ({
          ...prev,
          current_segment_index: nextSegmentIndex,
          timer_mode: 'stopped'
        }));

        // Auto-start next segment after brief pause
        setTimeout(() => {
          if (!timerActive) {
            startTimer();
          }
        }, 2000);
      }
    } else if (config.format === 'structured' && config.structured) {
      if (sessionState.current_segment_index < config.structured.segments.length - 1) {
        // More segments to go
        setSessionState(prev => ({
          ...prev,
          current_segment_index: prev.current_segment_index + 1,
          timer_mode: 'stopped'
        }));

        // Auto-start next segment after brief pause
        setTimeout(() => {
          if (!timerActive) {
            startTimer();
          }
        }, 2000);
      } else {
        // All segments complete
        handleWorkoutComplete();
      }
    }
  };

  // Skip current segment
  const skipSegment = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    setTimerActive(false);
    handleSegmentComplete();
  };

  // Handle workout complete
  const handleWorkoutComplete = () => {
    setShowCompleteModal(true);

    const completionData: CardioCompletionData = {
      exercise_id: exercise.exercise_id,
      session_id: `session_${Date.now()}`,
      completed_at: new Date().toISOString(),
      total_duration_seconds: sessionState.total_elapsed_seconds,
      total_distance: sessionState.distance_covered,
      distance_unit: config.simple?.distance_unit || 'miles',

      segment_completions: sessionState.completed_segments.map((seg, index) => ({
        segment_index: seg.segment_index,
        segment_type: getCurrentSegmentType(seg.segment_index),
        completed: true,
        duration_actual: seg.duration,
        distance_actual: seg.distance
      }))
    };

    onComplete(completionData);
  };

  const getCurrentSegmentType = (index: number): 'warmup' | 'work' | 'recovery' | 'cooldown' => {
    const segment = config.format === 'intervals' && config.intervals
      ? config.intervals.segments[index % config.intervals.segments.length]
      : config.format === 'structured' && config.structured
        ? config.structured.segments[index]
        : null;

    return segment?.type || 'work';
  };

  // Render progress bar
  const renderProgressBar = () => {
    const totalDuration = getTotalDuration();
    const progress = totalDuration > 0
      ? (sessionState.total_elapsed_seconds / totalDuration) * 100
      : 0;

    return (
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div
          className="bg-primary h-2 rounded-full transition-all duration-300"
          style={{ width: `${Math.min(progress, 100)}%` }}
        />
      </div>
    );
  };

  // Render interval timeline
  const renderIntervalTimeline = () => {
    if (config.format !== 'intervals' || !config.intervals) return null;

    const segments = config.intervals.segments;
    const totalRepeats = config.intervals.repeat_count || 1;

    return (
      <div className="bg-gray-50 p-4 rounded-lg">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium">
            Round {sessionState.current_repeat} of {totalRepeats}
          </span>
          <span className="text-xs text-gray-500">
            {sessionState.completed_segments.length} segments completed
          </span>
        </div>
        <div className="flex gap-1">
          {segments.map((segment, index) => {
            const isActive = index === (sessionState.current_segment_index % segments.length);
            const isCompleted = sessionState.completed_segments.some(
              s => s.segment_index % segments.length === index &&
                s.repeat_number === sessionState.current_repeat
            );

            return (
              <div
                key={index}
                className={`flex-1 h-8 rounded flex items-center justify-center text-xs font-medium ${isActive ? 'bg-primary text-white' :
                  isCompleted ? 'bg-green-500 text-white' :
                    'bg-gray-200 text-gray-600'
                  }`}
              >
                {segment.type === 'work' ? 'Work' :
                  segment.type === 'recovery' ? 'Rest' :
                    segment.type === 'warmup' ? 'Warm' :
                      'Cool'}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const currentSegment = getCurrentSegment();
  const nextSegment = getNextSegment();

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Activity className="h-6 w-6 text-primary" />
          <div>
            <h3 className="font-semibold text-lg">{exercise.official_title}</h3>
            <p className="text-sm text-gray-600">
              {config.format === 'simple' ? 'Steady State' :
                config.format === 'intervals' ? 'Interval Training' :
                  'Structured Workout'}
            </p>
          </div>
        </div>
        <button
          onClick={onSkip}
          className="text-gray-400 hover:text-gray-600"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Progress */}
      <div className="mb-6">
        {renderProgressBar()}
        <div className="flex justify-between mt-2 text-sm text-gray-600">
          <span>{Math.floor(sessionState.total_elapsed_seconds / 60)}:{(sessionState.total_elapsed_seconds % 60).toString().padStart(2, '0')}</span>
          <span>{Math.floor(getTotalDuration() / 60)} min total</span>
        </div>
      </div>

      {/* Interval Timeline */}
      {renderIntervalTimeline()}

      {/* Current Segment Info */}
      {currentSegment && (
        <div className="bg-blue-50 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-blue-900">
              Current: {currentSegment.type.toUpperCase()}
            </span>
            <span className="text-sm text-blue-700">
              {currentSegment.intensity || 'moderate'} intensity
            </span>
          </div>
          <div className="text-2xl font-bold text-blue-900">
            {Math.floor(sessionState.timer_remaining / 60)}:
            {(sessionState.timer_remaining % 60).toString().padStart(2, '0')}
          </div>
          {currentSegment.duration_seconds && (
            <div className="mt-2 w-full bg-blue-100 rounded-full h-2">
              <div
                className="bg-blue-500 h-2 rounded-full transition-all duration-1000"
                style={{
                  width: `${((currentSegment.duration_seconds - sessionState.timer_remaining) /
                    currentSegment.duration_seconds) * 100}%`
                }}
              />
            </div>
          )}
        </div>
      )}

      {/* Next Segment Preview */}
      {nextSegment && (
        <div className="bg-gray-50 rounded-lg p-3 mb-6">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">Up Next:</span>
            <span className="text-sm font-medium">
              {nextSegment.type} - {nextSegment.duration_seconds}s @ {nextSegment.intensity}
            </span>
          </div>
        </div>
      )}

      {/* Timer Controls */}
      <div className="flex items-center justify-center gap-4 mb-6">
        {!timerActive && sessionState.timer_mode === 'stopped' && (
          <>
            <button
              onClick={startTimer}
              className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 flex items-center gap-2"
            >
              <Play className="h-5 w-5" />
              <span>Start</span>
            </button>
            {/* Add Mark Complete button when timer hasn't started but some time elapsed */}
            {sessionState.total_elapsed_seconds > 0 && (
              <button
                onClick={() => {
                  const completionData: CardioCompletionData = {
                    exercise_id: exercise.exercise_id,
                    session_id: `session_${Date.now()}`,
                    completed_at: new Date().toISOString(),
                    total_duration_seconds: sessionState.total_elapsed_seconds,
                    total_distance: currentDistance ? parseFloat(currentDistance) : undefined,
                    distance_unit: config.simple?.distance_unit || 'miles',
                    simple_completion: {
                      duration_completed: sessionState.total_elapsed_seconds / 60,
                      distance_completed: currentDistance ? parseFloat(currentDistance) : undefined,
                      pace_actual: currentPace || undefined
                    }
                  };
                  onComplete(completionData);
                }}
                className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
              >
                <CheckCircle className="h-5 w-5" />
                <span>Mark Complete</span>
              </button>
            )}
          </>
        )}
        {timerActive && (
          <>
            <button
              onClick={pauseTimer}
              className="px-6 py-3 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 flex items-center gap-2"
            >
              <Pause className="h-5 w-5" />
              <span>Pause</span>
            </button>
            <button
              onClick={skipSegment}
              className="px-6 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 flex items-center gap-2"
            >
              <SkipForward className="h-5 w-5" />
              <span>Skip</span>
            </button>
            {/* Add Complete Early button while timer is running */}
            <button
              onClick={() => {
                // Stop timer and complete
                if (timerRef.current) {
                  clearInterval(timerRef.current);
                }
                setTimerActive(false);

                const completionData: CardioCompletionData = {
                  exercise_id: exercise.exercise_id,
                  session_id: `session_${Date.now()}`,
                  completed_at: new Date().toISOString(),
                  total_duration_seconds: sessionState.total_elapsed_seconds,
                  total_distance: currentDistance ? parseFloat(currentDistance) : undefined,
                  distance_unit: config.simple?.distance_unit || 'miles',
                  simple_completion: {
                    duration_completed: sessionState.total_elapsed_seconds / 60,
                    distance_completed: currentDistance ? parseFloat(currentDistance) : undefined,
                    pace_actual: currentPace || undefined
                  }
                };
                onComplete(completionData);
              }}
              className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
            >
              <CheckCircle className="h-5 w-5" />
              <span>Complete Early</span>
            </button>
          </>
        )}
        {!timerActive && sessionState.timer_mode === 'paused' && (
          <>
            <button
              onClick={startTimer}
              className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 flex items-center gap-2"
            >
              <Play className="h-5 w-5" />
              <span>Resume</span>
            </button>
            {/* Add Mark Complete button when paused */}
            <button
              onClick={() => {
                const completionData: CardioCompletionData = {
                  exercise_id: exercise.exercise_id,
                  session_id: `session_${Date.now()}`,
                  completed_at: new Date().toISOString(),
                  total_duration_seconds: sessionState.total_elapsed_seconds,
                  total_distance: currentDistance ? parseFloat(currentDistance) : undefined,
                  distance_unit: config.simple?.distance_unit || 'miles',
                  simple_completion: {
                    duration_completed: sessionState.total_elapsed_seconds / 60,
                    distance_completed: currentDistance ? parseFloat(currentDistance) : undefined,
                    pace_actual: currentPace || undefined
                  }
                };
                onComplete(completionData);
              }}
              className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
            >
              <CheckCircle className="h-5 w-5" />
              <span>Mark Complete</span>
            </button>
          </>
        )}
      </div>

      {/* Manual Tracking Inputs */}
      <div className="border-t pt-4">
        <h4 className="text-sm font-medium mb-3">Track Your Progress</h4>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm text-gray-600 mb-1">Distance</label>
            <div className="flex gap-2">
              <input
                type="number"
                step="0.1"
                value={currentDistance}
                onChange={(e) => setCurrentDistance(e.target.value)}
                placeholder="0.0"
                className="flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <select className="px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary">
                <option value="miles">mi</option>
                <option value="km">km</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">Current Pace</label>
            <input
              type="text"
              value={currentPace}
              onChange={(e) => setCurrentPace(e.target.value)}
              placeholder="8:30/mi"
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
        </div>
      </div>

      {/* Completion Modal */}
      {showCompleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Workout Complete!</h3>
              <p className="text-gray-600 mb-4">
                Great job! You completed {Math.floor(sessionState.total_elapsed_seconds / 60)} minutes
                {sessionState.distance_covered && ` and ${sessionState.distance_covered} miles`}.
              </p>
              <button
                onClick={() => setShowCompleteModal(false)}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Local storage keys
const STORAGE_KEY_PREFIX = 'prism_session_';

export function SessionPlayer() {
  const { assignmentId, date } = useParams<{ assignmentId: string; date: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // State
  const [loading, setLoading] = useState(true);
  const [assignment, setAssignment] = useState<any>(null);
  const [prescription, setPrescription] = useState<any>(null);
  const [currentExercise, setCurrentExercise] = useState<any>(null);
  const [sessionState, setSessionState] = useState<SessionState | null>(null);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [existingSession, setExistingSession] = useState<SessionState | null>(null);
  const [viewMode, setViewMode] = useState<'interactive' | 'log'>('interactive');
  const [isInitializing, setIsInitializing] = useState(false);

  // Location selection state
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [showNoLocationsPrompt, setShowNoLocationsPrompt] = useState(false);
  const [locations, setLocations] = useState<PatientLocation[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<PatientLocation | null>(null);
  const [equipmentGaps, setEquipmentGaps] = useState<ExerciseEquipmentCheck[]>([]);
  const [proposedSubstitutions, setProposedSubstitutions] = useState<Map<string, ExerciseSubstitution[]>>(new Map());
  const [locationLoading, setLocationLoading] = useState(false);
  const [pendingPrescription, setPendingPrescription] = useState<any>(null);

  // Current set tracking
  const [currentReps, setCurrentReps] = useState<string>('');
  const [currentWeight, setCurrentWeight] = useState<string>('');
  const [currentDifficulty, setCurrentDifficulty] = useState<'too_easy' | 'just_right' | 'too_hard'>('just_right');
  const [currentNotes, setCurrentNotes] = useState<string>('');

  // Timer
  const [timerActive, setTimerActive] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Audio
  const audioContextRef = useRef<AudioContext | null>(null);
  const soundBufferRef = useRef<AudioBuffer | null>(null);

  // Completion
  const isCompletingRef = useRef(false);


  useEffect(() => {
    if (assignmentId && date && !isInitializing) {
      initializeSession();
    }
  }, [assignmentId, date]);

  // Initialize audio context and load sounds
  useEffect(() => {
    if (typeof window !== 'undefined' && window.AudioContext) {
      audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
      loadSound('https://storage.googleapis.com/glide-prod.appspot.com/uploads-v2/yx58r6aGlO9tUxUcF6qZ/pub/5NOkMOzp8x6Fgh6qsuTP.wav')
        .then(buffer => {
          soundBufferRef.current = buffer;
        });
    }
  }, []);

  // Load sound helper
  const loadSound = async (url: string): Promise<AudioBuffer> => {
    const response = await fetch(url);
    const buffer = await response.arrayBuffer();
    return audioContextRef.current!.decodeAudioData(buffer);
  };

  // Play sound helper
  const playSound = (volume: number = 1) => {
    if (!audioContextRef.current || !soundBufferRef.current || !sessionState?.audioEnabled) return;

    const sourceNode = audioContextRef.current.createBufferSource();
    const gainNode = audioContextRef.current.createGain();

    sourceNode.buffer = soundBufferRef.current;
    sourceNode.connect(gainNode);
    gainNode.connect(audioContextRef.current.destination);
    gainNode.gain.setValueAtTime(volume, audioContextRef.current.currentTime);
    sourceNode.start(0);
  };


  const isCardioExercise = (exercise: any): boolean => {
    return exercise?.is_cardio_activity === true ||
      exercise?.exercise_id?.startsWith('cardio_');
  };

  // Check if current exercise is a substitute
  const getSubstitutionForExercise = (exerciseId: string): ExerciseSubstitution | undefined => {
    return sessionState?.locationContext?.substitutions?.find(
      (sub) => sub.substitute_exercise_id === exerciseId
    );
  };

  // Initialize session
  const initializeSession = async () => {
    if (isInitializing) return;
    setIsInitializing(true);
    setLoading(true);

    try {
      // Check if switching mode from log mode
      const searchParams = new URLSearchParams(window.location.search);
      const isSwitchingMode = searchParams.get('mode') === 'switch';
      // Check for existing session in localStorage
      const storageKey = `${STORAGE_KEY_PREFIX}${assignmentId}_${date}`;
      const savedSession = localStorage.getItem(storageKey);

      if (savedSession) {
        const parsed = JSON.parse(savedSession);
        if (isSwitchingMode) {
          // Auto-resume when switching from log mode
          await loadExerciseFromState(parsed);
        } else {
          // Show resume modal for normal navigation
          setExistingSession(parsed);
          setShowResumeModal(true);
        }
      } else {
        // Check if session exists for today in database
        const sessions = await patientApiService.getAssignmentSessions(assignmentId!, {
          session_date: date
        }).catch(() => []);

        if (sessions && sessions.length > 0 && sessions[0].status === 'in_progress') {
          if (!isSwitchingMode) {
            setShowResumeModal(true);
          } else {
            // If switching modes, start fresh
            await startNewSession();
          }
        } else {
          // No existing session - check for locations before starting
          await checkLocationsAndStart();
        }
      }
    } catch (error) {
      console.error('Error initializing session:', error);
    } finally {
      setLoading(false);
      setIsInitializing(false);
    }
  };

  // Check for locations and show modal or start directly
  // Returns: 'session_started' if session was started, 'modal_shown' if a modal was displayed
  const checkLocationsAndStart = async (): Promise<'session_started' | 'modal_shown'> => {
    try {
      // Check if location was pre-selected (e.g., from Exercise Now)
      const preSelectedLocationId = searchParams.get('locationId');

      // Fetch user's locations
      const userLocations = await patientApiService.getMyLocations().catch(() => []);
      setLocations(userLocations);

      if (preSelectedLocationId && userLocations.length > 0) {
        // Location already selected (from Exercise Now) - skip modal
        const preSelectedLoc = userLocations.find((l: PatientLocation) => l.id === preSelectedLocationId);
        if (preSelectedLoc) {
          setSelectedLocation(preSelectedLoc);
          // Start session directly with pre-selected location (no substitution checks needed
          // since Exercise Now already generated workout based on this location's equipment)
          await startNewSession({
            locationId: preSelectedLoc.id,
            locationName: preSelectedLoc.name,
            locationType: preSelectedLoc.location_type,
            equipmentAvailable: preSelectedLoc.equipment,
            substitutions: []
          });
          return 'session_started';
        }
      }

      if (userLocations.length > 0) {
        // Pre-select default location
        const defaultLoc = userLocations.find((l: PatientLocation) => l.is_default) || userLocations[0];
        setSelectedLocation(defaultLoc);

        // Show location modal for quick-start
        setShowLocationModal(true);
        return 'modal_shown';
      } else {
        // No locations configured - prompt user to add one or skip
        setShowNoLocationsPrompt(true);
        return 'modal_shown';
      }
    } catch (error) {
      console.error('Error checking locations:', error);
      // Fall back to starting without location
      await startNewSession();
      return 'session_started';
    }
  };

  // Handle location selection and check equipment
  const handleLocationSelect = async (location: PatientLocation) => {
    setSelectedLocation(location);
    setLocationLoading(true);
    setEquipmentGaps([]);
    setProposedSubstitutions(new Map());

    try {
      // If we don't have the prescription yet, fetch it first
      let prescriptionData = pendingPrescription;
      if (!prescriptionData) {
        const assignmentData = await patientApiService.getExerciseAssignment(assignmentId!);
        setAssignment(assignmentData);

        if (assignmentData.assignment_type === 'workout_plan') {
          const progress = await patientApiService.getAssignmentProgress(assignmentId!).catch(() => ({ current_week: 1 }));
          const currentWeek = progress.current_week || 1;
          const schedule = await patientApiService.getWorkoutWeekSchedule(
            assignmentData.workout_plan_id,
            currentWeek
          );
          const jsDayOfWeek = new Date(date! + 'T00:00:00').getDay();
          const isoDayOfWeek = jsDayOfWeek === 0 ? 7 : jsDayOfWeek;
          const scheduleOverrides = assignmentData.schedule_overrides || [];
          const todaySchedule = schedule.find((s: any) => {
            const override = scheduleOverrides.find(
              (o: any) => o.week_number === currentWeek && o.new_day === isoDayOfWeek
            );
            if (override) return s.day_of_week === override.original_day;
            return s.day_of_week === isoDayOfWeek;
          });
          prescriptionData = todaySchedule?.exercise_prescription;
        } else if (assignmentData.assignment_type === 'rehab_program') {
          const progress = await patientApiService.getAssignmentProgress(assignmentId!).catch(() => ({ current_phase: 1 }));
          const prescriptions = await patientApiService.getRehabPhaseSchedule(
            assignmentData.rehab_program_id,
            progress.current_phase || 1
          );
          prescriptionData = prescriptions[0]?.exercise_prescription;
        } else {
          prescriptionData = await patientApiService.getExerciseProgram(assignmentData.exercise_prescription_id);
        }
        setPendingPrescription(prescriptionData);
      }

      // Extract exercise IDs from prescription
      const exerciseIds: string[] = [];
      if (prescriptionData?.modules) {
        prescriptionData.modules.forEach((mod: any) => {
          mod.exercises?.forEach((ex: any) => {
            exerciseIds.push(ex.id || ex.exercise_id);
          });
        });
      } else if (prescriptionData?.exercises) {
        prescriptionData.exercises.forEach((ex: any) => {
          exerciseIds.push(ex.id || ex.exercise_id);
        });
      }

      // Check equipment compatibility
      if (exerciseIds.length > 0 && location.equipment.length > 0) {
        const result = await patientApiService.checkExerciseEquipment(
          exerciseIds,
          location.equipment
        ).catch(() => ({ compatible: exerciseIds, incompatible: [] }));

        if (result.incompatible && result.incompatible.length > 0) {
          setEquipmentGaps(result.incompatible);

          // Fetch substitutes for incompatible exercises
          const subsMap = new Map<string, ExerciseSubstitution[]>();
          await Promise.all(
            result.incompatible.map(async (gap: ExerciseEquipmentCheck) => {
              try {
                const subs = await patientApiService.findExerciseSubstitutes({
                  exercise_id: gap.exercise_id,
                  available_equipment: location.equipment
                });
                if (subs.substitutes && subs.substitutes.length > 0) {
                  subsMap.set(gap.exercise_id, subs.substitutes.map((s: any) => ({
                    original_exercise_id: gap.exercise_id,
                    original_exercise_name: gap.exercise_name,
                    substitute_exercise_id: s.exercise.id,
                    substitute_exercise_name: s.exercise.official_title || s.exercise.display_name,
                    substitution_reason: 'equipment_unavailable' as const,
                    equipment_required: gap.equipment_required,
                    equipment_missing: gap.equipment_missing,
                    match_score: s.match_score
                  })));
                }
              } catch (e) {
                console.warn('Failed to find substitutes for', gap.exercise_id);
              }
            })
          );
          setProposedSubstitutions(subsMap);
        }
      }
    } catch (error) {
      console.error('Error checking equipment:', error);
    } finally {
      setLocationLoading(false);
    }
  };

  // Confirm location and start session with substitutions
  const handleLocationConfirm = async (
    location: PatientLocation,
    acceptedSubstitutions: ExerciseSubstitution[]
  ) => {
    // Set loading to true BEFORE hiding modal to prevent flash of error state
    setLoading(true);
    setShowLocationModal(false);

    // Start session with location context
    await startNewSession({
      locationId: location.id,
      locationName: location.name,
      locationType: location.location_type,
      equipmentAvailable: location.equipment,
      substitutions: acceptedSubstitutions
    });

    // Session is ready, hide loading
    setLoading(false);
  };

  // Handle resume choice
  const handleResumeChoice = async (resume: boolean) => {
    // Set loading to true BEFORE hiding modal to prevent flash of error state
    setLoading(true);
    setShowResumeModal(false);

    if (resume && existingSession) {
      await loadExerciseFromState(existingSession);
      setLoading(false);
    } else {
      // Clear existing and check for locations before starting new
      const storageKey = `${STORAGE_KEY_PREFIX}${assignmentId}_${date}`;
      localStorage.removeItem(storageKey);
      await checkLocationsAndStart();
      setLoading(false);
    }
  };

  // Convert JS day to ISO day (0-6 to 1-7)
  const jsToISODay = (jsDay: number): number => jsDay === 0 ? 7 : jsDay;
  const isoDayToJS = (isoDay: number): number => isoDay === 7 ? 0 : isoDay;

  // Find the correct schedule item for a given day, accounting for overrides
  const findScheduleForDay = (
    schedule: any[],
    isoDayOfWeek: number,
    overrides: any[] | undefined,
    weekNumber: number
  ): any => {
    // Check if any override moves a workout TO this day
    const override = overrides?.find(
      (o: any) => o.week_number === weekNumber && o.new_day === isoDayOfWeek
    );

    if (override) {
      // Find the workout that was originally on the override's original_day
      return schedule.find((s: any) => s.day_of_week === override.original_day);
    }

    // Check if there's an override moving this day's workout away
    const movedAway = overrides?.find(
      (o: any) => o.week_number === weekNumber && o.original_day === isoDayOfWeek
    );

    if (movedAway) {
      // This day's original workout was moved elsewhere, check if something else moved here
      // (already handled above, so if we get here, nothing is scheduled for this day)
      return null;
    }

    // No override affects this day, use the original schedule
    return schedule.find((s: any) => s.day_of_week === isoDayOfWeek);
  };

  // Start new session
  const startNewSession = async (locationContext?: {
    locationId: string;
    locationName: string;
    locationType: string;
    equipmentAvailable: string[];
    substitutions: ExerciseSubstitution[];
  }) => {
    // Use pending prescription if already fetched, otherwise fetch
    let assignmentData = assignment;
    if (!assignmentData) {
      assignmentData = await patientApiService.getExerciseAssignment(assignmentId!);
      setAssignment(assignmentData);
    }

    // Fetch prescription based on assignment type
    let prescriptionData;
    if (assignmentData.assignment_type === 'workout_plan') {
      const progress = await patientApiService.getAssignmentProgress(assignmentId!).catch(() => ({ current_week: 1 }));
      const currentWeek = progress.current_week || 1;
      const schedule = await patientApiService.getWorkoutWeekSchedule(
        assignmentData.workout_plan_id,
        currentWeek
      );

      // Convert JavaScript day to ISO day for comparison
      const jsDayOfWeek = new Date(date! + 'T00:00:00').getDay(); // 0=Sun, 1=Mon...6=Sat
      const isoDayOfWeek = jsToISODay(jsDayOfWeek); // 1=Mon...7=Sun

      // Apply schedule overrides to find the correct workout for today
      const scheduleOverrides = assignmentData.schedule_overrides || [];
      const todaySchedule = findScheduleForDay(schedule, isoDayOfWeek, scheduleOverrides, currentWeek);
      prescriptionData = todaySchedule?.exercise_prescription;

      if (!prescriptionData) {
        console.warn(`No workout scheduled for ISO day ${isoDayOfWeek}`);
      }
    } else if (assignmentData.assignment_type === 'rehab_program') {
      const progress = await patientApiService.getAssignmentProgress(assignmentId!).catch(() => ({ current_phase: 1 }));
      const prescriptions = await patientApiService.getRehabPhaseSchedule(
        assignmentData.rehab_program_id,
        progress.current_phase || 1
      );
      prescriptionData = prescriptions[0]?.exercise_prescription;
    } else {
      prescriptionData = await patientApiService.getExerciseProgram(assignmentData.exercise_prescription_id);
    }

    setPrescription(prescriptionData);

    // Calculate total exercises
    let totalExercises = 0;
    if (prescriptionData.modules) {
      prescriptionData.modules.forEach((module: any) => {
        totalExercises += module.exercises?.length || 0;
      });
    } else if (prescriptionData.exercises) {
      totalExercises = prescriptionData.exercises.length;
    }

    // Create session record in database (with optional location context)
    const sessionRecord = await patientApiService.createSession(assignmentId!, {
      prescriptionId: prescriptionData.id,
      sessionDate: date!,
      sessionType: 'interactive',
      totalExercises,
      // Location context
      locationId: locationContext?.locationId,
      locationName: locationContext?.locationName,
      equipmentAvailable: locationContext?.equipmentAvailable,
      substitutions: locationContext?.substitutions
    });

    // Initialize session state
    const newState: SessionState = {
      sessionId: sessionRecord.id,
      assignmentId: assignmentId!,
      prescriptionId: prescriptionData.id,
      sessionDate: date!,
      startedAt: new Date().toISOString(),
      currentModuleIndex: 0,
      currentExerciseIndex: 0,
      currentSet: 1,
      timerMode: 'stopped',
      timerDuration: 0,
      timerRemaining: 0,
      audioEnabled: true,
      completedExercises: [],
      skippedExercises: [],
      totalExercisesPrescribed: totalExercises,
      // Location context
      locationContext: locationContext ? {
        location_id: locationContext.locationId,
        location_name: locationContext.locationName,
        location_type: locationContext.locationType as any,
        equipment_available: locationContext.equipmentAvailable,
        equipment_gaps: equipmentGaps.map(g => g.equipment_missing).flat(),
        substitutions: locationContext.substitutions
      } : undefined
    };

    setSessionState(newState);
    saveSessionToStorage(newState);

    // Load first exercise
    loadExerciseFromPrescription(prescriptionData, 0, 0);
  };

  // Load exercise from saved state
  const loadExerciseFromState = async (state: SessionState) => {
    const assignmentData = await patientApiService.getExerciseAssignment(state.assignmentId);
    setAssignment(assignmentData);

    // Fetch prescription
    let prescriptionData;
    if (assignmentData.assignment_type === 'workout_plan') {
      const progress = await patientApiService.getAssignmentProgress(state.assignmentId).catch(() => ({ current_week: 1 }));
      const currentWeek = progress.current_week || 1;
      const schedule = await patientApiService.getWorkoutWeekSchedule(
        assignmentData.workout_plan_id,
        currentWeek
      );

      // Convert JavaScript day to ISO day for comparison
      const jsDayOfWeek = new Date(state.sessionDate).getDay();
      const isoDayOfWeek = jsToISODay(jsDayOfWeek);

      // Apply schedule overrides to find the correct workout
      const scheduleOverrides = assignmentData.schedule_overrides || [];
      const todaySchedule = findScheduleForDay(schedule, isoDayOfWeek, scheduleOverrides, currentWeek);
      prescriptionData = todaySchedule?.exercise_prescription;
    } else if (assignmentData.assignment_type === 'rehab_program') {
      const progress = await patientApiService.getAssignmentProgress(state.assignmentId).catch(() => ({ current_phase: 1 }));
      const prescriptions = await patientApiService.getRehabPhaseSchedule(
        assignmentData.rehab_program_id,
        progress.current_phase || 1
      );
      prescriptionData = prescriptions[0]?.exercise_prescription;
    } else {
      prescriptionData = await patientApiService.getExerciseProgram(assignmentData.exercise_prescription_id);
    }

    setPrescription(prescriptionData);
    setSessionState(state);

    if (prescriptionData) {
      loadExerciseFromPrescription(prescriptionData, state.currentModuleIndex, state.currentExerciseIndex);
    }
  };

  // Load specific exercise from prescription
  const loadExerciseFromPrescription = (prescription: any, moduleIndex: number, exerciseIndex: number) => {
    let exercise;

    if (prescription.modules && prescription.modules.length > 0) {
      const module = prescription.modules[moduleIndex];
      if (module && module.exercises && module.exercises[exerciseIndex]) {
        exercise = {
          ...module.exercises[exerciseIndex],
          exercise_id: module.exercises[exerciseIndex].id || module.exercises[exerciseIndex].exercise_id,
          module_id: module.id,
          module_name: module.name,
          module_type: module.module_type
        };
      }
    } else if (prescription.exercises && prescription.exercises.length > 0) {
      exercise = {
        ...prescription.exercises[exerciseIndex],
        exercise_id: prescription.exercises[exerciseIndex].id || prescription.exercises[exerciseIndex].exercise_id// <-- Map id to exercise_id
      };
    }

    if (exercise) {
      setCurrentExercise(exercise);
      setCurrentReps(exercise.parameters?.reps?.toString() || '');
      setCurrentWeight(exercise.parameters?.weight || '');
    }
  };

  // Save session to localStorage
  const saveSessionToStorage = (state: SessionState) => {
    const storageKey = `${STORAGE_KEY_PREFIX}${assignmentId}_${date}`;
    localStorage.setItem(storageKey, JSON.stringify(state));
  };

  // Timer functions
  const startTimer = (duration: number, mode: 'work' | 'rest' | 'hold') => {
    if (!sessionState) return;

    setSessionState(prev => ({
      ...prev!,
      timerMode: mode,
      timerDuration: duration,
      timerRemaining: duration
    }));

    setTimerActive(true);

    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    timerRef.current = setInterval(() => {
      setSessionState(prev => {
        if (!prev) return null;

        const newRemaining = prev.timerRemaining - 1;

        if (newRemaining === 3 || newRemaining === 2 || newRemaining === 1) {
          playSound(1);
        }

        if (newRemaining <= 0) {
          clearInterval(timerRef.current!);
          setTimerActive(false);
          playSound(1);

          if (mode === 'rest') {
            if (prev.currentSet < (currentExercise?.parameters?.sets || 1)) {
              return {
                ...prev,
                currentSet: prev.currentSet + 1,
                timerMode: 'stopped',
                timerRemaining: 0
              };
            } else {
              handleNextExercise();
            }
          }

          return {
            ...prev,
            timerMode: 'stopped',
            timerRemaining: 0
          };
        }

        return {
          ...prev,
          timerRemaining: newRemaining
        };
      });
    }, 1000);
  };

  const pauseTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      setTimerActive(false);
      setSessionState(prev => prev ? { ...prev, timerMode: 'paused' } : null);
    }
  };

  const resumeTimer = () => {
    if (!sessionState || sessionState.timerRemaining <= 0) return;
    startTimer(sessionState.timerRemaining, sessionState.timerMode as any);
  };

  const skipRest = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    setTimerActive(false);

    if (!sessionState || !currentExercise) return;

    if (sessionState.currentSet < (currentExercise.parameters?.sets || 1)) {
      // Moving to next set
      setSessionState(prev => prev ? {
        ...prev,
        currentSet: prev.currentSet + 1,
        timerMode: 'stopped',
        timerRemaining: 0
      } : null);
    } else {
      // Moving to next exercise - no need to finalize here as it's already done in completeSet
      handleNextExercise();
    }
  };

  // Complete current set
  const completeSet = () => {
    if (!sessionState || !currentExercise) return;

    // Create a unique key for this exercise
    const exerciseKey = `${currentExercise.exercise_id}_${sessionState.currentModuleIndex}_${sessionState.currentExerciseIndex}`;

    // Find or create exercise completion for THIS specific exercise instance
    let exerciseIndex = sessionState.completedExercises.findIndex(
      e => e.exercise_id === currentExercise.exercise_id &&
        e.module_id === currentExercise.module_id &&
        e.completed_at === exerciseKey // Use as unique identifier
    );

    if (exerciseIndex === -1) {
      // Create new completion entry for this exercise
      const newCompletion: CompletedExercise = {
        exercise_id: currentExercise.exercise_id,
        exercise_name: currentExercise.official_title || currentExercise.display_name || 'Unknown Exercise',
        module_id: currentExercise.module_id,
        sets_completed: 1,
        reps_completed: [currentReps],
        weight_used: [currentWeight],
        difficulty_rating: currentDifficulty,
        notes: currentNotes,
        completed_at: exerciseKey // Using as unique ID temporarily
      };
      sessionState.completedExercises.push(newCompletion);
    } else {
      // Update existing entry for this exercise
      const exerciseCompletion = sessionState.completedExercises[exerciseIndex];
      exerciseCompletion.reps_completed.push(currentReps);
      exerciseCompletion.weight_used.push(currentWeight);
      exerciseCompletion.sets_completed = exerciseCompletion.reps_completed.length;
      exerciseCompletion.difficulty_rating = currentDifficulty;
      exerciseCompletion.notes = currentNotes;
    }

    saveSessionToStorage(sessionState);

    // Check if more sets remain
    if (sessionState.currentSet < (currentExercise.parameters?.sets || 1)) {
      // Start rest timer for next set
      const restTime = currentExercise.parameters?.rest_time;
      if (restTime && restTime > 0) {
        startTimer(restTime, 'rest');
      } else {
        setSessionState(prev => prev ? {
          ...prev,
          currentSet: prev.currentSet + 1
        } : null);
      }
    } else {
      // All sets completed for this exercise
      // Finalize the exercise completion with proper timestamp
      const exerciseIndex = sessionState.completedExercises.findIndex(
        e => e.completed_at === exerciseKey
      );
      if (exerciseIndex !== -1) {
        sessionState.completedExercises[exerciseIndex].completed_at = new Date().toISOString();
      }

      // Rest before next exercise
      const restTime = currentExercise.parameters?.rest_time || 0;
      startTimer(restTime, 'rest');
    }
  };

  // Navigation functions
  const handleNextExercise = () => {
    if (!prescription) return;

    setSessionState(prev => {
      if (!prev) return prev;

      let nextModuleIndex = prev.currentModuleIndex;
      let nextExerciseIndex = prev.currentExerciseIndex + 1;

      // Check if we've completed all exercises
      let isComplete = false;

      if (prescription.modules && prescription.modules.length > 0) {
        if (nextExerciseIndex >= prescription.modules[nextModuleIndex].exercises.length) {
          nextModuleIndex++;
          nextExerciseIndex = 0;

          if (nextModuleIndex >= prescription.modules.length) {
            isComplete = true;
          }
        }
      } else if (prescription.exercises) {
        if (nextExerciseIndex >= prescription.exercises.length) {
          isComplete = true;
        }
      }

      // If complete, trigger completion after state update
      if (isComplete) {
        setTimeout(() => completeSession(), 0);
        return prev; // Don't update state if completing
      }

      const newState: SessionState = {
        ...prev,
        currentModuleIndex: nextModuleIndex,
        currentExerciseIndex: nextExerciseIndex,
        currentSet: 1,
        timerMode: 'stopped' as const,
        timerRemaining: 0,
        timerDuration: 0
      };

      // Save and load next exercise after state update
      setTimeout(() => {
        saveSessionToStorage(newState);
        loadExerciseFromPrescription(prescription, nextModuleIndex, nextExerciseIndex);
      }, 0);

      return newState;
    });
  };

  const handlePreviousExercise = () => {
    if (!sessionState || !prescription) return;

    let prevModuleIndex = sessionState.currentModuleIndex;
    let prevExerciseIndex = sessionState.currentExerciseIndex - 1;

    if (prescription.modules) {
      if (prevExerciseIndex < 0) {
        prevModuleIndex--;
        if (prevModuleIndex < 0) return;
        prevExerciseIndex = prescription.modules[prevModuleIndex].exercises.length - 1;
      }
    } else {
      if (prevExerciseIndex < 0) return;
    }

    const newState = {
      ...sessionState,
      currentModuleIndex: prevModuleIndex,
      currentExerciseIndex: prevExerciseIndex,
      currentSet: 1
    };

    setSessionState(newState);
    saveSessionToStorage(newState);
    loadExerciseFromPrescription(prescription, prevModuleIndex, prevExerciseIndex);
  };

  const handleSkipExercise = (reason: string) => {
    if (!sessionState || !currentExercise) return;

    const skipped: SkippedExercise = {
      exercise_id: currentExercise.exercise_id,
      reason,
      skipped_at: new Date().toISOString()
    };

    sessionState.skippedExercises.push(skipped);
    saveSessionToStorage(sessionState);
    handleNextExercise();
  };

  // Complete session
  const completeSession = async () => {
    if (!sessionState || isCompletingRef.current) return;
    
    // Set flag to prevent double completion
    isCompletingRef.current = true;
  
    try {
      // Clean up the completed exercises (fix the temporary IDs)
      const cleanedExercises = sessionState.completedExercises.map(ex => {
        const baseExercise = {
          exercise_id: ex.exercise_id,
          exercise_name: ex.exercise_name || 'Unknown Exercise',
          module_id: ex.module_id || null,
          prescription_id: sessionState.prescriptionId,
          patient_id: assignment?.patient_id || null,
          sets_completed: ex.sets_completed,
          reps_completed: ex.reps_completed,
          weight_used: ex.weight_used,
          difficulty_rating: ex.difficulty_rating || null,
          notes: ex.notes || null,
          completed_at: ex.completed_at.includes('_') ?
            new Date().toISOString() : ex.completed_at
        };

        // Add cardio-specific fields if present
        if ((ex as any).is_cardio) {
          return {
            ...baseExercise,
            is_cardio: true,
            cardio_data: (ex as any).cardio_data
          };
        }

        return baseExercise;
      });
  
      const skippedExercises = sessionState.skippedExercises.map(skip => ({
        exercise_id: skip.exercise_id,
        reason: skip.reason,
        skipped_at: skip.skipped_at
      }));
  
      await patientApiService.completeSession(sessionState.sessionId!, {
        exercises_completed: cleanedExercises.length,
        exercises_skipped: skippedExercises.length,
        session_notes: '',
        session_rating: null,
        completed_exercises: cleanedExercises,
        skipped_exercises: skippedExercises
      });
  
      const storageKey = `${STORAGE_KEY_PREFIX}${assignmentId}_${date}`;
      localStorage.removeItem(storageKey);
  
      navigate(`/programs/${assignmentId}`);
    } catch (error) {
      console.error('Error saving session:', error);
      // Reset flag on error so user can retry
      isCompletingRef.current = false;
    }
  };

  // Calculate progress
  const calculateProgress = () => {
    if (!prescription || !sessionState) return { current: 0, total: 0, percentage: 0 };

    let totalExercises = 0;
    let currentPosition = 0;

    if (prescription.modules) {
      prescription.modules.forEach((module: any, mIndex: number) => {
        module.exercises.forEach((exercise: any, eIndex: number) => {
          totalExercises++;
          if (mIndex < sessionState.currentModuleIndex ||
            (mIndex === sessionState.currentModuleIndex && eIndex <= sessionState.currentExerciseIndex)) {
            currentPosition++;
          }
        });
      });
    } else if (prescription.exercises) {
      totalExercises = prescription.exercises.length;
      currentPosition = sessionState.currentExerciseIndex + 1;
    }

    return {
      current: currentPosition,
      total: totalExercises,
      percentage: totalExercises > 0 ? Math.round((currentPosition / totalExercises) * 100) : 0
    };
  };

  const progress = calculateProgress();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Resume Modal
  if (showResumeModal) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full">
          <h2 className="text-xl font-semibold mb-4">Resume Session?</h2>
          <p className="text-gray-600 mb-6">
            You have an incomplete session from earlier. Would you like to resume where you left off or start a new session?
          </p>
          <div className="flex space-x-3">
            <button
              onClick={() => handleResumeChoice(true)}
              className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
            >
              Resume Session
            </button>
            <button
              onClick={() => handleResumeChoice(false)}
              className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
            >
              Start New
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Location Selection Modal
  if (showLocationModal) {
    return (
      <LocationSelectionModal
        locations={locations}
        equipmentGaps={equipmentGaps}
        proposedSubstitutions={proposedSubstitutions}
        selectedLocation={selectedLocation}
        onSelectLocation={handleLocationSelect}
        onConfirm={handleLocationConfirm}
        onCreateLocation={() => navigate('/settings/locations')}
        onCancel={() => {
          setShowLocationModal(false);
          navigate(-1);
        }}
        loading={locationLoading}
      />
    );
  }

  // No Locations Prompt
  if (showNoLocationsPrompt) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <MapPin className="h-8 w-8 text-primary" />
            </div>
            <h2 className="text-xl font-semibold mb-2">Set Up Your Workout Location</h2>
            <p className="text-gray-600">
              Add a workout location to get exercise substitutions based on your available equipment.
            </p>
          </div>
          <div className="space-y-3">
            <button
              onClick={() => navigate('/settings/locations?returnTo=' + encodeURIComponent(window.location.pathname))}
              className="w-full px-4 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 font-medium"
            >
              Add a Location
            </button>
            <button
              onClick={async () => {
                setLoading(true);
                setShowNoLocationsPrompt(false);
                await startNewSession();
                setLoading(false);
              }}
              className="w-full px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
            >
              Skip for Now
            </button>
            <button
              onClick={() => {
                setShowNoLocationsPrompt(false);
                navigate(-1);
              }}
              className="w-full px-4 py-2 text-gray-500 hover:text-gray-700 text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!currentExercise || !sessionState) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader title="Session Error" showBack />
        <div className="text-center py-8">
          <p className="text-gray-600">Unable to load exercise session</p>
        </div>
      </div>
    );
  }

  // Render Log View
  if (viewMode === 'log') {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader
          title="Log Workout"
          showBack
          actions={
            <button
              onClick={() => setViewMode('interactive')}
              className="flex items-center space-x-2 px-3 py-2 bg-white border rounded-lg hover:bg-gray-50"
            >
              <Activity className="h-4 w-4" />
              <span className="text-sm">Interactive Mode</span>
            </button>
          }
        />
        <div className="max-w-4xl mx-auto px-4 py-6">
          <p className="text-center text-gray-600">Log view coming soon...</p>
        </div>
      </div>
    );
  }

  // Render Interactive View
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">
                {currentExercise.module_name || 'Exercise'} • {progress.current} of {progress.total}
              </p>
              <div className="mt-1 w-48 bg-gray-200 rounded-full h-2">
                <div
                  className="bg-primary h-2 rounded-full transition-all"
                  style={{ width: `${progress.percentage}%` }}
                />
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setSessionState(prev => prev ? { ...prev, audioEnabled: !prev.audioEnabled } : null)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                {sessionState.audioEnabled ? (
                  <Volume2 className="h-5 w-5" />
                ) : (
                  <VolumeX className="h-5 w-5 text-gray-400" />
                )}
              </button>
              <button
                onClick={() => navigate(`/programs/${assignmentId}/log/${date}`)}
                className="p-2 hover:bg-gray-100 rounded-lg"
                title="Switch to Log View"
              >
                <FileText className="h-5 w-5" />
              </button>
              <button
                onClick={() => navigate(`/programs/${assignmentId}`)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* CHECK IF CARDIO AND RENDER APPROPRIATELY */}
        {(currentExercise.is_cardio_activity ||
          currentExercise.exercise_id?.startsWith('cardio_')) ? (

          // CARDIO EXERCISE - Use CardioPlayer
          <CardioPlayer
            exercise={currentExercise}
            config={currentExercise.parameters?.cardio_config || {
              format: 'simple',
              simple: {
                duration_minutes: currentExercise.parameters?.duration_minutes || 20,
                intensity: 'moderate'
              }
            }}
            onComplete={(data) => {
              // Handle cardio completion
              const completion = {
                exercise_id: currentExercise.exercise_id,
                exercise_name: currentExercise.official_title || currentExercise.display_name || 'Cardio Activity',
                module_id: currentExercise.module_id,
                sets_completed: 1,
                reps_completed: [],
                weight_used: [],
                cardio_data: data,
                is_cardio: true,
                completed_at: new Date().toISOString()
              };


              // Add to completed exercises
              setSessionState(prev => {
                if (!prev) return prev;
                const newState = {
                  ...prev,
                  completedExercises: [...prev.completedExercises, completion]
                };

                // Save to storage after state update
                setTimeout(() => saveSessionToStorage(newState), 0);

                return newState;
              });

              // Move to next exercise
              handleNextExercise();
            }}
            onSkip={() => {
              handleSkipExercise('Skipped cardio exercise');
            }}
            audioEnabled={sessionState?.audioEnabled || false}
          />
        ) : (
          // REGULAR EXERCISE - Keep existing code
          <>
            {/* Exercise Media */}
            <div className="bg-white rounded-lg shadow-sm p-4">
              {currentExercise.exercise_gif ? (
                <img
                  src={currentExercise.exercise_gif}
                  alt={currentExercise.official_title}
                  className="w-full h-64 object-contain rounded-lg"
                />
              ) : currentExercise.image_url ? (
                <img
                  src={currentExercise.image_url}
                  alt={currentExercise.official_title}
                  className="w-full h-64 object-contain rounded-lg"
                />
              ) : (
                <div className="w-full h-64 bg-gray-100 rounded-lg flex items-center justify-center">
                  <Activity className="h-16 w-16 text-gray-400" />
                </div>
              )}

              {currentExercise.video_url && (
                <button
                  onClick={() => setShowVideoModal(true)}
                  className="mt-3 w-full flex items-center justify-center space-x-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
                >
                  <Video className="h-4 w-4" />
                  <span>Watch Video</span>
                </button>
              )}
            </div>

            {/* Exercise Info */}
            <div className="bg-white rounded-lg shadow-sm p-4">
              <h2 className="text-xl font-semibold mb-2">
                {currentExercise.official_title || 'Exercise'}
              </h2>
              {currentExercise.parameters?.instructions && (
                <ul className="mb-3 space-y-1">
                  {String(currentExercise.parameters.instructions).split('\n').filter((line: string) => line.trim()).map((line: string, i: number) => (
                    <li key={i} className="text-sm text-gray-700 flex items-start">
                      <span className="mr-2 text-primary">•</span>
                      <span>{line.replace(/^[-•]\s*/, '')}</span>
                    </li>
                  ))}
                </ul>
              )}
              <div className="flex items-center space-x-4 text-sm text-gray-600">
                <span>Set {sessionState.currentSet} of {currentExercise.parameters?.sets || 1}</span>
                {currentExercise.parameters?.reps && (
                  <span>Target: {currentExercise.parameters.reps} reps</span>
                )}
                {currentExercise.parameters?.weight && (
                  <span>@ {currentExercise.parameters.weight}</span>
                )}
              </div>

              {/* Substitution Banner - show if this exercise is a substitute */}
              {(() => {
                const exerciseId = currentExercise.id || currentExercise.exercise_id;
                const substitution = getSubstitutionForExercise(exerciseId);
                return substitution ? (
                  <div className="mt-3">
                    <SubstitutionBanner substitution={substitution} compact />
                  </div>
                ) : null;
              })()}
            </div>

            {/* Timer Section */}
            {(currentExercise.parameters?.hold_time || timerActive || sessionState.timerMode === 'paused') && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="text-center">
                  <div className="text-4xl font-bold text-primary mb-4">
                    {Math.floor(sessionState.timerRemaining / 60)}:
                    {(sessionState.timerRemaining % 60).toString().padStart(2, '0')}
                  </div>
                  <div className="text-sm text-gray-600 mb-4">
                    {sessionState.timerMode === 'work' && 'Work Time'}
                    {sessionState.timerMode === 'rest' && 'Rest Time'}
                    {sessionState.timerMode === 'hold' && 'Hold Time'}
                    {sessionState.timerMode === 'stopped' && 'Ready'}
                  </div>
                  <div className="flex items-center justify-center space-x-4">
                    {!timerActive && sessionState.timerMode === 'stopped' && currentExercise.parameters?.hold_time && (
                      <button
                        onClick={() => startTimer(currentExercise.parameters.hold_time, 'hold')}
                        className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 flex items-center space-x-2"
                      >
                        <Play className="h-5 w-5" />
                        <span>Start Timer</span>
                      </button>
                    )}
                    {timerActive && (
                      <>
                        <button
                          onClick={pauseTimer}
                          className="px-6 py-3 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 flex items-center space-x-2"
                        >
                          <Pause className="h-5 w-5" />
                          <span>Pause</span>
                        </button>
                        {sessionState.timerMode === 'rest' && (
                          <button
                            onClick={skipRest}
                            className="px-6 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 flex items-center space-x-2"
                          >
                            <FastForward className="h-5 w-5" />
                            <span>Skip Rest</span>
                          </button>
                        )}
                      </>
                    )}
                    {!timerActive && sessionState.timerMode === 'paused' && (
                      <button
                        onClick={resumeTimer}
                        className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 flex items-center space-x-2"
                      >
                        <Play className="h-5 w-5" />
                        <span>Resume</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Tracking Inputs */}
            <div className="bg-white rounded-lg shadow-sm p-4">
              <h3 className="font-medium mb-3">Log Your Set</h3>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Reps Completed</label>
                    <input
                      type="text"
                      value={currentReps}
                      onChange={(e) => setCurrentReps(e.target.value)}
                      placeholder={currentExercise.parameters?.reps?.toString() || "Reps"}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Weight Used</label>
                    <input
                      type="text"
                      value={currentWeight}
                      onChange={(e) => setCurrentWeight(e.target.value)}
                      placeholder={currentExercise.parameters?.weight || "e.g., 20 lbs"}
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-gray-600 mb-2">Difficulty</label>
                  <div className="flex items-center justify-around">
                    {['too_easy', 'just_right', 'too_hard'].map((level) => (
                      <button
                        key={level}
                        onClick={() => setCurrentDifficulty(level as any)}
                        className={`px-4 py-2 rounded-lg transition-colors ${currentDifficulty === level
                          ? 'bg-primary text-white'
                          : 'bg-gray-100 hover:bg-gray-200'
                          }`}
                      >
                        {level === 'too_easy' && '😊 Too Easy'}
                        {level === 'just_right' && '😐 Just Right'}
                        {level === 'too_hard' && '😣 Too Hard'}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-gray-600 mb-1">Notes (Optional)</label>
                  <textarea
                    value={currentNotes}
                    onChange={(e) => setCurrentNotes(e.target.value)}
                    placeholder="Any notes about this exercise..."
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    rows={2}
                  />
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-between">
              <button
                onClick={handlePreviousExercise}
                disabled={sessionState.currentModuleIndex === 0 && sessionState.currentExerciseIndex === 0}
                className="px-4 py-2 bg-white border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                <SkipBack className="h-4 w-4" />
                <span>Previous</span>
              </button>

              <button
                onClick={completeSet}
                className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 flex items-center space-x-2"
              >
                <CheckCircle className="h-5 w-5" />
                <span>
                  {sessionState.currentSet < (currentExercise.parameters?.sets || 1)
                    ? 'Complete Set'
                    : 'Complete Exercise'}
                </span>
              </button>

              <button
                onClick={() => {
                  const reason = prompt('Reason for skipping?');
                  if (reason) handleSkipExercise(reason);
                }}
                className="px-4 py-2 bg-white border rounded-lg hover:bg-gray-50 flex items-center space-x-2"
              >
                <SkipForward className="h-4 w-4" />
                <span>Skip</span>
              </button>
            </div>
          </>
        )}
      </div>

      {/* Video Modal */}
      {showVideoModal && currentExercise.video_url && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">Exercise Video</h3>
              <button
                onClick={() => setShowVideoModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4">
              <video
                src={currentExercise.video_url}
                controls
                className="w-full rounded-lg"
                autoPlay
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}